// src/router.js
import { isAuthenticated, getUserRole, hasAccess } from "./lib/auth.js";

// Static routes (exact match)
const staticRoutes = {
  "#/": { module: "./pages/login.js", role: null },
  "#/login": { module: "./pages/login.js", role: null },

  "#/agent/": { module: "./pages/agent/assignments.js", role: "agent" },
  "#/agent/uploads": {
    module: "./pages/agent/upload-status.js",
    role: "agent",
  },
  "#/agent/protocol": { module: "./pages/agent/protocol.js", role: "agent" },

  "#/dashboard/": { module: "./pages/dashboard/queue.js", role: "reviewer" },

  "#/ops/": { module: "./pages/ops/dashboard.js", role: "ops" },
  "#/ops/dataset": { module: "./pages/ops/dataset.js", role: "ops" },
  "#/ops/eval": { module: "./pages/ops/eval.js", role: "ops" },
  "#/ops/knowledge": { module: "./pages/ops/knowledge.js", role: "ops" },
  "#/ops/api": { module: "./pages/ops/api-console.js", role: "ops" },
  "#/ops/status": { module: "./pages/ops/status-board.js", role: "ops" },
  "#/ops/system": { module: "./pages/ops/system-health.js", role: "ops" },
  "#/ops/deleted": { module: "./pages/ops/deleted.js", role: "ops" },

  // Public pages (no auth)
  "#/docs": { module: "./pages/public/docs.js", role: null },
  "#/privacy": { module: "./pages/customer/privacy.js", role: null },
  "#/terms": { module: "./pages/customer/terms.js", role: null },
};

// Dynamic routes (pattern match with :param)
const dynamicRoutes = [
  {
    pattern: /^#\/agent\/job\/(.+)$/,
    module: "./pages/agent/job-detail.js",
    role: "agent",
    paramName: "id",
  },
  {
    pattern: /^#\/agent\/indemnity\/(.+)$/,
    module: "./pages/agent/indemnity-form.js",
    role: "agent",
    paramName: "id",
  },
  {
    pattern: /^#\/agent\/capture\/(.+)$/,
    module: "./pages/agent/capture.js",
    role: "agent",
    paramName: "id",
  },
  {
    pattern: /^#\/agent\/abnormal\/(.+)$/,
    module: "./pages/agent/abnormal.js",
    role: "agent",
    paramName: "id",
  },
  {
    pattern: /^#\/dashboard\/review\/(.+)$/,
    module: "./pages/dashboard/review.js",
    role: "reviewer",
    paramName: "id",
  },
  {
    pattern: /^#\/dashboard\/report-preview\/(.+)$/,
    module: "./pages/dashboard/report-preview.js",
    role: "reviewer",
    paramName: "id",
  },
  {
    pattern: /^#\/ops\/inspection\/(.+)$/,
    module: "./pages/ops/inspection-detail.js",
    role: "ops",
    paramName: "id",
  },
];

// Eager-load all page modules for production compatibility
const pageModules = import.meta.glob("./pages/**/*.js");

// Current route params (accessible by pages)
export let currentParams = {};

function matchRoute(hash) {
  // Try static first
  if (staticRoutes[hash]) {
    currentParams = {};
    return staticRoutes[hash];
  }

  // Try dynamic
  for (const route of dynamicRoutes) {
    const match = hash.match(route.pattern);
    if (match) {
      currentParams = { [route.paramName]: match[1] };
      return { module: route.module, role: route.role };
    }
  }

  return null;
}

export async function navigate(hash) {
  if (!hash || hash === "#") {
    hash = "#/";
  }

  // Try matching route first to check if it's public
  const route = matchRoute(hash);

  const authenticated = await isAuthenticated();

  // Allow public routes (role === null) without auth
  if (!authenticated && hash !== "#/" && hash !== "#/login") {
    if (!route || route.role !== null) {
      window.location.hash = "#/login";
      return;
    }
  }

  const userRole = authenticated ? await getUserRole() : null;

  if (!route) {
    console.error(`No route found for ${hash}`);
    if (!authenticated) {
      window.location.hash = "#/login";
    } else {
      redirectToRoleHome(userRole);
    }
    return;
  }

  // Hierarchy-aware role check: CEO can access all routes
  if (route.role && !hasAccess(userRole, route.role)) {
    console.warn(
      `Access denied: ${userRole} tried to access ${hash} (requires ${route.role})`,
    );
    redirectToRoleHome(userRole);
    return;
  }

  try {
    const loader = pageModules[route.module];
    if (!loader) {
      throw new Error(`Module not found: ${route.module}`);
    }
    const pageModule = await loader();
    const container = document.getElementById("app");
    if (!container) {
      console.error("Main container #app not found");
      return;
    }
    container.innerHTML = "";
    pageModule.render(container, currentParams);
    sessionStorage.removeItem("pr_stale_reload");
  } catch (err) {
    console.error(`Failed to load page module ${route.module}:`, err);

    // Stale-deploy self-healing: after a deploy, an open tab may hold chunk
    // hashes that no longer exist — dynamic imports then fail. One forced
    // reload fetches the fresh index + chunks (guarded to prevent loops).
    const staleChunk =
      /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(
        err?.message || "",
      );
    if (staleChunk && !sessionStorage.getItem("pr_stale_reload")) {
      sessionStorage.setItem("pr_stale_reload", "1");
      window.location.reload();
      return;
    }

    const container = document.getElementById("app");
    if (container) {
      container.innerHTML = `
        <div class="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
          <div class="text-center">
            <p class="text-[11px] font-semibold text-white/30 uppercase tracking-[0.3em] mb-3" style="font-family:var(--font-mono)">Route error</p>
            <h1 class="text-xl font-bold text-white mb-2">This page failed to load</h1>
            <p class="text-[13px] text-white/40 mb-6">Usually a fresh deploy — a reload fixes it.</p>
            <button onclick="window.location.reload()" class="px-5 h-10 bg-white text-black rounded-lg text-[13px] font-semibold">Reload</button>
          </div>
        </div>
      `;
    }
  }
}

function redirectToRoleHome(role) {
  const roleHomes = {
    agent: "#/ops/",
    ceo: "#/ops/",
    reviewer: "#/dashboard/",
    ops: "#/ops/",
  };
  window.location.hash = roleHomes[role] || "#/login";
}

export function initRouter() {
  window.addEventListener("hashchange", () => {
    navigate(window.location.hash);
  });

  const initialHash = window.location.hash || "#/";
  navigate(initialHash);
}
